/**
 * MODULE: platform/browser/frontend/utils/browserLaunch.ts
 * PURPOSE: Stash CRM/social context and navigate to /browser for automated remote sessions.
 */

import type { BrowserMissionId } from '../data/browserMissions';

export const BROWSER_LAUNCH_STORAGE_KEY = 'brazn.browser.launch';

export type BrowserLaunchPayload = {
  mission: BrowserMissionId;
  task?: string;
  context?: string;
  customStartUrl?: string;
  autoRun?: boolean;
};

/** Cached consume result — survives React Strict Mode double-mount in dev. */
let consumedLaunchCache: BrowserLaunchPayload | null | undefined;

export function stashBrowserLaunch(payload: BrowserLaunchPayload): void {
  if (typeof sessionStorage === 'undefined') return;
  consumedLaunchCache = undefined;
  sessionStorage.setItem(BROWSER_LAUNCH_STORAGE_KEY, JSON.stringify(payload));
}

export function consumeBrowserLaunch(): BrowserLaunchPayload | null {
  if (consumedLaunchCache !== undefined) {
    return consumedLaunchCache;
  }

  if (typeof sessionStorage === 'undefined') {
    consumedLaunchCache = null;
    return null;
  }

  const raw = sessionStorage.getItem(BROWSER_LAUNCH_STORAGE_KEY);
  sessionStorage.removeItem(BROWSER_LAUNCH_STORAGE_KEY);
  if (!raw) {
    consumedLaunchCache = null;
    return null;
  }

  try {
    consumedLaunchCache = JSON.parse(raw) as BrowserLaunchPayload;
  } catch {
    consumedLaunchCache = null;
  }
  return consumedLaunchCache;
}

/** Test-only reset for unit tests. */
export function resetBrowserLaunchConsumeCache(): void {
  consumedLaunchCache = undefined;
}

export function openBrowserWithLaunch(
  payload: BrowserLaunchPayload,
  navigate: (path: string) => void,
): void {
  stashBrowserLaunch({ ...payload, autoRun: payload.autoRun ?? Boolean(payload.task) });
  navigate('/browser');
}
